/*
  Message Form Controller

  EMITTED EVENTS
    update-success (document) Emitted when a form is submitted successfully or is canceled
      detail: {
        el:         the root element of this controller instance,
        message_id: the parent-message-id data attribute value
      }
*/

import { adjustStickyHeader, createCustomEvent } from '../../shared/helpers';
import { messageFeedFor, updateDraft } from './messageHelpers';

export default class extends Controller {
  static targets = ['messageForm', 'button', 'notifySelect', 'usersInRolesForSelect', 'openMessageHeader', 'messageTitleField'];

  connect() {
    App.Helpers.addAjaxBeforeHandler(this.messageFormTarget, () => false)
    this.addChangeEventListeners();
    this.initializeSwitchChangeEvent();
    this.makeOpenMessageHeaderSticky();
    App.Helpers.listenToCustomEvent(document, 'dirty-form', this.markDirtyForm.bind(this));
    this.isParentMessage && this.messageTitleFieldTarget.focus();
    this.messageFeed = messageFeedFor(this.element)
    updateDraft(this.messageFormTarget)
    App.Helpers.listenToCustomEvent(document, "message--file-upload-complete", this.submitIfAllUploadsComplete.bind(this))
  }

  disconnect() {
    window.removeEventListener('scroll', this.adjustStickyHeader);
  }

  submitIfAllUploadsComplete(event) {
    const { el } = event.detail
    if (this.element.contains(el) && this.pendingUploadCount == 0) {
      this.submitForm(this.messageFormTarget)
    }
  }

  //
  // ACTION HANDLERS
  //

  submitHandler(event) {
    event.preventDefault();
    const button = this.buttonTarget;
    if (!button.disabled) {
      button.dataset.submitted = true;
      button.disabled = true;
      button.classList.add('disabled');
      this.submitForm(this.messageFormTarget)
    }
  }

  cancelForm(event) {
    event.preventDefault();
    this.enableNewMessageButton();
    this.messageFormTarget.innerHTML = App.spinner();
    this.deleteDraft();
    if (this.renderURL.length > 0) {
      App.Helpers.ajaxRequest({ url: this.renderURL, successHandler: this.handleFormClosure.bind(this) });
    } else {
      this.element.closest('.inline-msg-container').remove();
    }
  }

  addAllProjectRolesToNotifyList(event) {
    event && event.preventDefault();
    App.DynamicSelect.setSelect2Data(this.notifySelectTarget, JSON.parse(this.usersInRolesForSelectTarget.value));
  }

  clearNotifyList(event) {
    event && event.preventDefault();
    App.DynamicSelect.setSelect2Data(this.notifySelectTarget, []);
  }

  //
  // PRIVATE INTERNAL FUNCTIONS
  //

  addChangeEventListeners() {
    ['change', 'propertychange', 'keyup', 'input', 'paste'].forEach(event => {
      this.messageFormTarget.addEventListener(event, this.markDirtyForm.bind(this));
    });
    App.DynamicSelect.addEventHandler(this.notifySelectTarget, 'change', this.markDirtyForm.bind(this));
  }

  initializeSwitchChangeEvent() {
    this.toggleSwitchContainer && this.toggleSwitchContainer.addEventListener('click', this.hideOrShowOpenMessageHeader.bind(this));
  }

  makeOpenMessageHeaderSticky() {
    this.openMessageHeaderTargets.length > 0 && window.addEventListener('scroll', this.adjustStickyHeader.bind(this));
  }

  markDirtyForm() {
    this.messageFormTarget.classList.add('dirty-form');
  }

  submitForm(form) {
    if (this.pendingUploadCount > 0) {
      this.buttonTarget.value = 'Waiting for uploads to complete...';
    } else {
      this.buttonTarget.value = "Please wait...";
      form.classList.remove('dirty-form');
      App.Helpers.ajaxRequest({
        url:    this.formAction,
        method: this.formMethod,
        data: App.Helpers.formDataAsString(this.messageFormTarget),
        successHandler: this.handleFormClosure.bind(this),
        errorHandler:   this.replaceFormWithResponse.bind(this)
      });
    }
  }

  handleFormClosure() {
    document.dispatchEvent(createCustomEvent('update-success', this.element, { message_id: this.parentMessageID }));
  }

  replaceFormWithResponse({ responseText }) {
    const { html } = JSON.parse(responseText);
    const container = this.element.closest('.inline-msg-container');
    container.innerHTML = html;
    App.addHandlers(container);
  }

  enableNewMessageButton() {
    const button = this.messageFeed.querySelector(".add-message-btn");
    if (button) {
      button.disabled = false;
      button.classList.remove('disabled');
    }
  }

  deleteDraft() {
    App.Helpers.ajaxRequest({
      url:    this.draftPostURL,
      method: 'delete',
      data:   {
        key: this.draftKey,
        draft_parent_id: this.draftParentID
      }
    });
  }

  //
  // EVENT HANDLERS
  //

  hideOrShowOpenMessageHeader(event) {
    const target = event.currentTarget;
    setTimeout(() => {
      if (target.dataset.switchState === 'on') {
        this.openMessageHeader.classList.add('show-open-header');
      } else {
        this.openMessageHeader.classList.remove('show-open-header');
      }
    }, 100);
  }

  adjustStickyHeader() {
    this.openMessageHeader && adjustStickyHeader({
      elementTop: this.topOfForm,
      elementBottom: this.bottomOfForm,
      element: this.openMessageHeader,
      width: this.formWidth
    });
  }

  //
  // GETTERS AND SETTERS
  //

  get openMessageHeader() {
    return this.openMessageHeaderTargets.length > 0 ? this.openMessageHeaderTarget : null;
  }

  get topOfForm() {
    return this.element.getBoundingClientRect().top + window.scrollY;
  }

  get bottomOfForm() {
    return this.topOfForm + this.element.offsetHeight;
  }

  get formWidth() {
    return this.element.offsetWidth;
  }

  get toggleSwitchContainer() {
    return this.element.querySelector('.toggle-switch-container');
  }

  get parentMessageID() {
    return this.data.get('parent-message-id');
  }

  get formAction() {
    return this.data.get('action');
  }

  get formMethod() {
    return this.data.get('method');
  }

  get renderURL() {
    return this.data.get('render-url') || '';
  }

  get draftPostURL() {
    return `/drafts/${this.draftKey}`;
  }

  get draftKey() {
    return this.data.get('draft-key');
  }

  get draftParentID() {
    return this.data.get('draft-parent-id');
  }

  get isParentMessage() {
    return ["1", 1, true, "true"].includes(this.data.get("is-parent"))
  }

  get pendingUploadCount() {
    return [...this.element.querySelectorAll(".pending-upload")].length
  }
};



// WEBPACK FOOTER //
// ./app/javascript/stimulusControllers/messages/message-form-controller.js